import { type RealtimeEventCbCancel } from '@doxyme/realtime';
import { type AwilixContainer } from 'awilix';
import { ServiceTokens } from '../../container';
import { type CommonRealtimeService } from '../call/realtime/commonRealtime.service';
import { type FileHostingService } from '../fileService';
import { RealtimeMessageType } from '../../features/realtime/realtime.types';
import { TimerObserver } from '../../libs/timerObserver';
import {
  filter,
  BehaviorSubject,
  type Subscription,
  Observable,
  share,
} from 'rxjs';
import { type CallService } from '../call';
import { getLogger } from '../../libs/logger';
import { type Logger } from 'loglevel';
import { timerKeys } from '../../shared/constants';

export type UploadTimerSteps = 'uploading' | 'uploaded' | 'cancelled' | 'error';

export type PhotoCaptureRequestStatus =
  | 'requested'
  | 'accepted'
  | 'rejected'
  | 'retaking'
  | 'capturing'
  | 'downloaded'
  | 'uploading'
  | 'uploaded'
  | 'cancelled'
  | 'error';

export type PhotoCaptureRequest = {
  requestId: string;
  source: string;
  destination: string;
  destinationUserId: string;
  status: PhotoCaptureRequestStatus;
  fileName?: string;
  preview?: Blob;
  photo?: Blob;
  error?: string;
};

type PhotoCaptureServiceDependencies = {
  [ServiceTokens.CommonRealtime]: CommonRealtimeService;
  [ServiceTokens.FileHosting]: FileHostingService;
  $container: AwilixContainer;
};

export class PhotoCaptureService {
  private readonly requestsSubject = new BehaviorSubject<
    PhotoCaptureRequest | undefined
  >(undefined);

  readonly request: Observable<PhotoCaptureRequest | undefined>;

  private readonly fileHosting: FileHostingService;

  private readonly realtime: CommonRealtimeService;

  private readonly container: AwilixContainer;

  private readonly logger: Logger;

  private readonly uploadTimer:
    | TimerObserver<PhotoCaptureRequestStatus>
    | undefined;

  private isUploadTimerStarted = false;

  private readonly ttl = 300; // 5 minutes

  constructor({
    commonRealtime,
    fileHosting,
    $container,
  }: PhotoCaptureServiceDependencies) {
    this.fileHosting = fileHosting;
    this.realtime = commonRealtime;
    this.container = $container;
    this.logger = getLogger('PhotoCaptureService');

    this.request = new Observable<PhotoCaptureRequest | undefined>(
      (observer) => {
        this.requestsSubject.subscribe(observer);

        const availableFileSubscriber = this.subscribeOnAvailableFile();
        const unsubscribeRealtime = this.subscribeOnRealtimeMessages();

        return () => {
          availableFileSubscriber.unsubscribe();
          unsubscribeRealtime();
          this.requestsSubject.next(undefined);
        };
      },
    ).pipe(share());

    this.uploadTimer = new TimerObserver<PhotoCaptureRequestStatus>(
      timerKeys.photoCaptureService,
      this.logger,
    );
  }

  async ask(
    source: string,
    destination = this.realtime.getIdentity().id,
    destinationUserId = this.realtime.getIdentity().userId,
  ): Promise<void> {
    this.logger.info('[PhotoCaptureService] requesting');

    if (destinationUserId === undefined) {
      throw new Error('Destination user is undefined');
    }

    const request = {
      source,
      destination,
      destinationUserId,
      status: 'requested',
    } as unknown as PhotoCaptureRequest;

    try {
      const requestId = await this.fileHosting.createSession(destinationUserId);

      await this.sync({ ...request, requestId });
    } catch (error) {
      void this.handleError(request, error);
    }
  }

  async accept(request: PhotoCaptureRequest): Promise<void> {
    return this.sync({ status: 'accepted' }, request);
  }

  async reject(request: PhotoCaptureRequest): Promise<void> {
    return this.sync({ status: 'rejected' }, request);
  }

  async retake(request: PhotoCaptureRequest): Promise<void> {
    this.logger.log('[PhotoCaptureService] retake', { request });
    return this.sync(
      {
        status: 'retaking',
        preview: undefined,
        photo: undefined,
        fileName: undefined,
      },
      request,
    );
  }

  async captureLocal(request: PhotoCaptureRequest): Promise<void> {
    try {
      this.requestsSubject.next({ ...request, status: 'capturing' });

      const preview = await this.getCall().captureSnapshot(request.source);

      const currentRequest = this.getCurrentRequest();
      if (!currentRequest) {
        return;
      }
      this.requestsSubject.next({ ...currentRequest, preview });
    } catch (error) {
      void this.handleError(request, error);
    }
  }

  async captureRemote(request: PhotoCaptureRequest): Promise<void> {
    return this.sync({ status: 'capturing' }, request);
  }

  private startUploadTimer(): void {
    if (this.isUploadTimerStarted) {
      return;
    }

    this.uploadTimer?.start('uploading');
    this.isUploadTimerStarted = true;
  }

  private finishUploadTimer(step: UploadTimerSteps): void {
    if (this.isUploadTimerStarted) {
      this.uploadTimer?.finish(step);
      this.isUploadTimerStarted = false;
    }
  }

  async upload(request: PhotoCaptureRequest, data: Blob): Promise<void> {
    this.logger.log('[PhotoCaptureService] upload');

    try {
      const fileName = `Photo-${Date.now()}.jpeg`;

      await this.sync({ status: 'uploading', fileName }, request);

      this.startUploadTimer();

      const url = await this.fileHosting.createUploadUrl({
        sessionId: request.requestId,
        fileName,
        contentType: 'image/jpeg',
        userId: request.destinationUserId,
        realtimeMemberId: request.destinationUserId,
        ttl: this.ttl,
      });

      // If there is a new request, we don't need to update
      let currentRequest = this.getCurrentRequest();
      if (
        currentRequest?.fileName !== fileName ||
        currentRequest.status !== 'uploading'
      ) {
        return;
      }

      await this.fileHosting.upload(
        url,
        new File([data], 'photo', {
          type: 'image/jpeg',
        }),
      );

      // If there is a new request, we don't need to update
      currentRequest = this.getCurrentRequest();
      if (
        currentRequest?.fileName !== fileName ||
        currentRequest.status !== 'uploading'
      ) {
        return;
      }

      this.finishUploadTimer('uploaded');

      await this.sync({ status: 'uploaded' });
    } catch (error) {
      this.finishUploadTimer('error');
      void this.handleError(request, error);
    }
  }

  async download(request: PhotoCaptureRequest, url: string): Promise<void> {
    this.logger.log('[PhotoCaptureService] download');

    try {
      const data = await this.fileHosting.download(url);

      const update: PhotoCaptureRequest = {
        ...request,
        status: 'downloaded',
        photo: new Blob([data], { type: 'image/jpeg' }),
      };

      this.requestsSubject.next(update);
    } catch (error: unknown) {
      void this.handleError(request, error);
    }
  }

  complete(request?: PhotoCaptureRequest): void {
    const currentRequest = this.requestsSubject.getValue();

    if (request?.requestId !== currentRequest?.requestId) {
      return;
    }

    this.requestsSubject.next(undefined);
  }

  async cancel(request: PhotoCaptureRequest): Promise<void> {
    this.finishUploadTimer('cancelled');

    return this.sync({ status: 'cancelled' }, request);
  }

  private async handleError(
    request: PhotoCaptureRequest,
    error: unknown,
  ): Promise<void> {
    this.logger.error('[PhotoCaptureService] error', error);

    return this.sync({ status: 'error', error: error?.toString() }, request);
  }

  getCurrentRequest(): PhotoCaptureRequest | undefined {
    return this.requestsSubject.getValue();
  }

  private async sync(
    data: Partial<PhotoCaptureRequest>,
    origin = this.requestsSubject.getValue(),
  ): Promise<void> {
    const update = { ...origin, ...data } as unknown as PhotoCaptureRequest;

    try {
      const receiver =
        update.source === this.getIdentityId()
          ? update.destination
          : update.source;

      await this.sendRealtimeRequest(receiver, update);
      this.requestsSubject.next(update);
    } catch (error: unknown) {
      this.requestsSubject.next({
        ...update,
        status: 'error',
        error: error?.toString(),
      });
    }
  }

  private async sendRealtimeRequest(
    userId: string,
    request: PhotoCaptureRequest,
  ): Promise<unknown> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { photo, preview, ...payload } = request;

    return this.realtime.send(userId, {
      messageType: RealtimeMessageType.PhotoCapture,
      payload,
    });
  }

  private getCall(): CallService {
    return this.container.resolve<CallService>(ServiceTokens.Call);
  }

  private getIdentityId(): string {
    return this.realtime.getIdentity().id;
  }

  private subscribeOnAvailableFile(): Subscription {
    return this.fileHosting.availableFile
      .pipe(filter((available) => Boolean(available.fileTransferSessionId)))
      .subscribe((available) => {
        const currentRequest = this.requestsSubject.getValue();
        if (currentRequest === undefined) {
          return;
        }

        if (currentRequest.fileName !== available.fileReady.name) {
          return;
        }

        void this.download(currentRequest, available.fileReady.downloadUrl);
      });
  }

  private subscribeOnRealtimeMessages(): RealtimeEventCbCancel {
    return this.realtime.on(RealtimeMessageType.PhotoCapture, (message) => {
      const update = message.payload as PhotoCaptureRequest;
      const currentRequest = this.requestsSubject.getValue();

      this.requestsSubject.next({
        ...currentRequest,
        ...update,
      });
    });
  }
}
